"use client"

import { useState } from "react"
import {
  Download,
  Loader2,
  CheckCircle2,
  User,
  Building2,
  Mail,
} from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"

const T = {
  es: {
    obligatorios: "Nombre, apellidos y email son obligatorios",
    emailMal: "Introduce un email válido",
    privacidadFalta: "Debes aceptar la política de privacidad",
    botonCorto: "Descargar PDF",
    boton: "Descargar diccionario en PDF",
    titulo: "Diccionario de Hostelería",
    subtitulo: "Rellena tus datos para descargar el PDF",
    nombre: "Nombre *",
    apellidos: "Apellidos *",
    empresa: "Nombre de empresa",
    email: "Email profesional *",
    heLeido: "He leído y acepto la",
    privacidad: "política de privacidad",
    rutaPrivacidad: "/legal/privacidad",
    novedades: "Acepto recibir novedades y consejos para mi restaurante por email.",
    procesando: "Procesando...",
    iniciada: "¡Descarga iniciada!",
    ahora: "Descargar ahora",
    sinSpam: "Sin spam. Tus datos están seguros.",
  },
  en: {
    obligatorios: "First name, surname and email are required",
    emailMal: "Enter a valid email address",
    privacidadFalta: "Please accept the privacy policy",
    botonCorto: "Download PDF",
    boton: "Download the dictionary as a PDF",
    titulo: "Hospitality Dictionary",
    subtitulo: "Fill in your details to download the PDF",
    nombre: "First name *",
    apellidos: "Surname *",
    empresa: "Company name",
    email: "Work email *",
    heLeido: "I have read and accept the",
    privacidad: "privacy policy",
    rutaPrivacidad: "/legal/privacidad",
    novedades: "I’d like to receive news and tips for my restaurant by email.",
    procesando: "Processing...",
    iniciada: "Download started!",
    ahora: "Download now",
    sinSpam: "No spam. Your details are safe.",
  },
}

interface GlossaryDownloadFormProps {
  compact?: boolean
  idioma?: Idioma
}

export default function GlossaryDownloadForm({
  compact = false,
  idioma = "es",
}: GlossaryDownloadFormProps) {
  const t = T[idioma]
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    empresa: "",
    email: "",
    acepta_privacidad: false,
    acepta_novedades: false,
  })
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!form.nombre.trim() || !form.apellidos.trim() || !form.email.trim()) {
      setError(t.obligatorios)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) {
      setError(t.emailMal)
      return
    }

    if (!form.acepta_privacidad) {
      setError(t.privacidadFalta)
      return
    }

    setLoading(true)
    try {
      await fetch("/api/glosario/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          recurso: "diccionario-hosteleria-pdf",
          ...(idioma === "en" ? { idioma } : {}),
        }),
      })

      setDone(true)
      const link = document.createElement("a")
      link.href = "/glosario/downloads/diccionario-hosteleria.pdf"
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setTimeout(() => {
        setDone(false)
        setIsOpen(false)
      }, 3000)
    } catch {
      const link = document.createElement("a")
      link.href = "/glosario/downloads/diccionario-hosteleria.pdf"
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setIsOpen(false)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-white rounded-xl font-semibold transition-all hover:opacity-90 group ${
          compact ? "px-6 py-3 text-base" : "px-8 py-4 text-lg"
        }`}
      >
        <Download className="w-5 h-5" />
        {compact ? t.botonCorto : t.boton}
      </button>
    )
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-lime-500 to-lime-600 flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 text-sm">
              {t.titulo}
            </h3>
            <p className="text-xs text-zinc-500">
              {t.subtitulo}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder={t.nombre}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder={t.apellidos}
                value={form.apellidos}
                onChange={(e) =>
                  setForm({ ...form, apellidos: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder={t.empresa}
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="email"
              placeholder={t.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_privacidad}
              onChange={(e) =>
                setForm({ ...form, acepta_privacidad: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              {t.heLeido}{" "}
              <a
                href={t.rutaPrivacidad}
                target="_blank"
                className="text-lime-600 underline hover:text-lime-700"
              >
                {t.privacidad}
              </a>
              . *
            </span>
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_novedades}
              onChange={(e) =>
                setForm({ ...form, acepta_novedades: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              {t.novedades}
            </span>
          </label>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={loading || done}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90 disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t.procesando}
              </>
            ) : done ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                {t.iniciada}
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                {t.ahora}
              </>
            )}
          </button>

          <p className="text-xs text-zinc-400 text-center">
            {t.sinSpam}
          </p>
        </form>
      </div>
    </div>
  )
}
